/**
 * The masking primitives. Pure, synchronous, no React.
 *
 * THE ONE RULE THAT MATTERS
 * -------------------------
 * Masking is applied to the *formatted string*, never to the number. `1_234_500`
 * paise stays `1_234_500` paise in state, in the request body, in every sum and
 * every chart. `maskMoney` takes a string that has already been formatted and
 * returns a different string. Nothing here touches arithmetic, so a hidden
 * total is still the right total the moment it is un-hidden.
 *
 * `maskAccountNumber` mirrors `mask_tail` in
 * `backend/api/app/services/privacy.py` rule for rule, including the awkward one:
 * a value SHORTER than the tail we would keep is masked entirely rather than
 * returned unchanged. A four-digit account number is exactly the case where
 * somebody would rely on masking and get nothing.
 */

import { shouldHideValues } from "./store";

/** U+2022 BULLET — the same character the server uses. */
export const MASK_CHAR = "•";

/**
 * What a hidden amount looks like.
 *
 * Bullets rather than a CSS blur: a blur is reversible from a screenshot with
 * a deconvolution filter and, more practically, still leaks magnitude — the
 * width of a blurred number tells you whether it is four digits or seven.
 * Fixed-width bullets leak neither. The `₹` is kept so the layout does not
 * jump and so it still reads as money.
 */
export const HIDDEN_AMOUNT = `₹${MASK_CHAR.repeat(5)}`;

/**
 * Replace a formatted amount with bullets.
 *
 * Takes the already-formatted string rather than the number so every formatter
 * — `formatINR`, `formatCompactINR`, the rent module's, home-loan's — routes
 * through one place, and so the currency prefix each of them chose is not
 * something this function has to know about.
 */
export function maskMoney(_formatted: string): string {
  return HIDDEN_AMOUNT;
}

/**
 * `"50100234564521"` -> `"•••• 4521"`.
 *
 * `null`/`undefined` in, `null` out: a missing account number must stay
 * missing. Rendering bullets for a number that does not exist tells the user
 * there is one to reveal.
 */
export function maskAccountNumber(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const cleaned = value.trim();
  if (!cleaned) return cleaned;

  const bullets = MASK_CHAR.repeat(4);
  if (cleaned.length <= 4) return MASK_CHAR.repeat(cleaned.length);
  return `${bullets} ${cleaned.slice(-4)}`;
}

/** The last four digits, ignoring spaces and dashes. `null` when there aren't four. */
export function last4(value: string | null | undefined): string | null {
  if (!value) return null;
  const digits = value.replace(/\D/g, "");
  return digits.length >= 4 ? digits.slice(-4) : null;
}

/**
 * Wrap an already-formatted money string in the hide-values check.
 *
 * The one-liner for the module-local `fmt`/`inr`/`rupees` helpers that predate
 * `<AmountText>` and that format money in a shape `formatINR` does not produce
 * (no symbol, a different grouping, a bare number inside a template literal).
 * Those helpers stay where they are; they just route their RETURN through here,
 * which is a single line each and keeps hide-values total rather than
 * approximate.
 *
 * Not for exports. CSV, Excel, PDF and the printable statement call the raw
 * formatter directly — a downloaded file full of bullets is a bug.
 */
export function maskIfHidden(formatted: string): string {
  return shouldHideValues() ? HIDDEN_AMOUNT : formatted;
}
